import React from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { useCart } from '../../contexts/CartContext';

function CartItemImage({ item }) {
  const [imgError, setImgError] = React.useState(false);
  const imageUrl = item.thumbnailUrl;

  if (item._isStatic || !imageUrl || imgError) {
    return (
      <div style={{
        width: 56,
        height: 56,
        borderRadius: 8,
        background: '#F3F4F6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: item._isStatic && item._staticEmoji ? 28 : 14,
        color: '#9CA3AF'
      }}>
        {item._isStatic && item._staticEmoji ? item._staticEmoji : (
          <Icon name="package" size={24} color="#9CA3AF" />
        )}
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={item.name}
      onError={() => setImgError(true)}
      style={{
        width: 56,
        height: 56,
        objectFit: 'cover',
        borderRadius: 8,
        flexShrink: 0,
        background: '#F3F4F6'
      }}
    />
  );
}

export function Cart({ onNavigate, isDrawer = false, onClose }) {
  const { brand } = useBrand();
  const { items, itemCount, subtotal, removeItem, updateQty, clearCart } = useCart();

  const content = (
    <div style={{
      background: isDrawer ? 'white' : '#F9FAFB',
      minHeight: isDrawer ? 'auto' : '100vh',
      padding: isDrawer ? '0' : '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: isDrawer ? '100%' : 900, margin: '0 auto' }}>
        {!isDrawer && (
          <>
            <Button
              variant="ghost"
              icon="arrowLeft"
              onClick={() => onNavigate(brand.isB2B ? 'catalog' : 'dashboard')}
              style={{ marginBottom: 24 }}
            >
              {brand.isB2B ? 'Back to Catalog' : 'Back to Dashboard'}
            </Button>
            <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
              {brand.isB2B ? 'Purchase Order Cart' : 'Your Cart'}
            </h1>
            <p style={{ color: '#6B7280', marginBottom: 32 }}>
              {itemCount} item{itemCount !== 1 ? 's' : ''} in your cart
            </p>
          </>
        )}

        {items.length === 0 ? (
          <Card>
            <div style={{ textAlign: 'center', padding: isDrawer ? '40px 20px' : '60px 20px' }}>
              <div style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: '#F3F4F6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Icon name="cart" size={40} color="#9CA3AF" />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>
                Your cart is empty
              </h3>
              <p style={{ color: '#6B7280', marginBottom: 24 }}>
                {brand.isB2B
                  ? 'Add products from the catalog to start a purchase order'
                  : 'Add items to your cart to get started'
                }
              </p>
              <Button
                icon={brand.isB2B ? 'grid' : 'package'}
                onClick={() => {
                  onNavigate(brand.isB2B ? 'catalog' : 'bundle');
                  onClose && onClose();
                }}
              >
                {brand.isB2B ? 'Browse Catalog' : 'Order Medications'}
              </Button>
            </div>
          </Card>
        ) : (
          <div style={{
            display: isDrawer ? 'block' : 'grid',
            gridTemplateColumns: isDrawer ? '1fr' : '2fr 1fr',
            gap: 24,
            alignItems: 'start'
          }}>
            <div>
              <Card>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 20
                }}>
                  <h2 style={{ fontSize: 20, fontWeight: 700, color: brand.c.navy, margin: 0 }}>
                    Cart Items
                  </h2>
                  <button
                    onClick={clearCart}
                    style={{
                      border: 'none',
                      background: 'none',
                      color: '#EF4444',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'Libre Franklin, sans-serif'
                    }}
                  >
                    Clear All
                  </button>
                </div>

                <div style={{ display: 'grid', gap: 12 }}>
                  {items.map(item => {
                    const itemKey = item.sku || item.id;
                    return (
                      <div
                        key={itemKey}
                        style={{
                          padding: 16,
                          background: '#F9FAFB',
                          borderRadius: 8,
                          display: 'flex',
                          gap: 16,
                          alignItems: 'center'
                        }}
                      >
                        <CartItemImage item={item} />

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: 700,
                            marginBottom: 4,
                            color: brand.c.navy,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {item.name}
                          </div>
                          <div style={{ fontSize: 13, color: '#6B7280' }}>
                            SKU: {itemKey}
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: brand.c.accent, marginTop: 4 }}>
                            ${item.price.toFixed(2)} each
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button
                            onClick={() => updateQty(itemKey, item.qty - 1)}
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 6,
                              border: '1px solid #E5E7EB',
                              background: 'white',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 16,
                              fontWeight: 700
                            }}
                          >
                            -
                          </button>
                          <span style={{
                            minWidth: 32,
                            textAlign: 'center',
                            fontWeight: 700,
                            fontSize: 14
                          }}>
                            {item.qty}
                          </span>
                          <button
                            onClick={() => updateQty(itemKey, item.qty + 1)}
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 6,
                              border: '1px solid #E5E7EB',
                              background: 'white',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 16,
                              fontWeight: 700
                            }}
                          >
                            +
                          </button>
                        </div>

                        <div style={{ textAlign: 'right', minWidth: 80 }}>
                          <div style={{ fontSize: 16, fontWeight: 700, color: brand.c.navy }}>
                            ${(item.price * item.qty).toFixed(2)}
                          </div>
                        </div>

                        <button
                          onClick={() => removeItem(itemKey)}
                          style={{
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            padding: 4,
                            flexShrink: 0
                          }}
                        >
                          <Icon name="trash" size={16} color="#EF4444" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>

            <div style={{ position: isDrawer ? 'static' : 'sticky', top: 100 }}>
              <Card>
                <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: brand.c.navy }}>
                  Order Summary
                </h2>

                <div style={{ marginBottom: 20 }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 12,
                    fontSize: 14
                  }}>
                    <span style={{ color: '#6B7280' }}>
                      Subtotal ({itemCount} item{itemCount !== 1 ? 's' : ''})
                    </span>
                    <span style={{ fontWeight: 600 }}>${subtotal.toFixed(2)}</span>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 12,
                    fontSize: 14
                  }}>
                    <span style={{ color: '#6B7280' }}>Shipping</span>
                    <span style={{ fontWeight: 600, color: '#10B981' }}>Calculated at checkout</span>
                  </div>

                  <div style={{ height: 1, background: '#E5E7EB', margin: '16px 0' }} />

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 20,
                    fontWeight: 700
                  }}>
                    <span>Total</span>
                    <span style={{ color: brand.c.accent }}>${subtotal.toFixed(2)}</span>
                  </div>
                </div>

                <Button
                  icon="arrowRight"
                  fullWidth
                  onClick={() => {
                    onNavigate('checkout');
                    onClose && onClose();
                  }}
                  style={{ marginBottom: 12 }}
                >
                  Proceed to Checkout
                </Button>

                <Button
                  variant="ghost"
                  fullWidth
                  onClick={() => {
                    onNavigate(brand.isB2B ? 'catalog' : 'dashboard');
                    onClose && onClose();
                  }}
                >
                  Continue Shopping
                </Button>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (isDrawer) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 2000,
        display: 'flex'
      }}>
        <div
          onClick={onClose}
          style={{
            flex: 1,
            background: 'rgba(0,0,0,0.4)',
            cursor: 'pointer'
          }}
        />
        <div style={{
          width: 480,
          maxWidth: '90vw',
          background: 'white',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
              Cart ({itemCount})
            </h2>
            <button
              onClick={onClose}
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                padding: 4
              }}
            >
              <Icon name="x" size={20} color="#6B7280" />
            </button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
            {content}
          </div>
        </div>
      </div>
    );
  }

  return content;
}
